import {cacheMemo, weakMemo} from '@packages/utilities/memo';
import {
  makeCell,
  getAdjacentCells,
  areAllCellsSameLocation,
  fillLayoutMatrixByLocation,
  deepCopyMatrix,
} from '../utils';
import {CELL_DIRECTIONS} from '../constants';

import type {
  CellId,
  Cell,
  MergeMetadata,
  CellLocation,
  LayoutMatrix,
  LayoutLocations,
  CellDirection,
} from '../types';
import computeLayoutMatrix from './computeLayoutMatrix';
import type {BreakpointID} from '@packages/systems/style/types';
import {LARGER_BREAKPOINTS} from '@packages/systems/style/media-queries';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import isRowEditingLocked from './isRowEditingLocked';

const mediaQueryMemo = cacheMemo(LARGER_BREAKPOINTS.length);
const variantStateMemo = cacheMemo(10);
// 50 items is somehow arbitrary, but it's unlikely that users will have more
// than 50 cells on the same page.
const CACHE_MAX_ITEMS = 50;
const cellIdMemo = cacheMemo(CACHE_MAX_ITEMS);

/**
 * Computes merge targets in every direction. Returns a nested object with
 * metadata to determine if the cell can be merged in that
 * direction and what are the adjacent cells target by the merge.
 *
 * Example return value:
 * {
 *   top: {
 *     canMerge: true,
 *     targetCells: ['500e1e86-c842-4eef-a1ca-1373e8feaf15', '16ff712e-8bf3-4d5c-9fa6-116dfa3a5fa2'],
 *   },
 *   left: { canMerge: false, targetCells: [] },
 *   ...
 * }
 */
const computeMergeCell = weakMemo((locations: LayoutLocations) => {
  const matrix = computeLayoutMatrix(locations);
  return cellIdMemo((cellId: CellId) =>
    mediaQueryMemo((mediaQuery: BreakpointID) =>
      variantStateMemo(
        (
          variantState: VariantStateKey | null
        ): Partial<Record<CellDirection, MergeMetadata>> => {
          const cell = makeCell(cellId, locations);
          return Object.values(CELL_DIRECTIONS).reduce<Record<string, any>>(
            (result, direction) => {
              result[direction] = isRowEditingLocked(mediaQuery, variantState)
                ? {canMerge: false, targetCells: []}
                : computeMergeInDirection(cell, direction, matrix, locations);
              return result;
            },
            {}
          );
        }
      )
    )
  );
});

/**
 * Computes the ability to merge a Cell in a given direction
 */
function computeMergeInDirection(
  cell: Cell | null | undefined,
  direction: CellDirection,
  matrix: LayoutMatrix,
  locations: LayoutLocations
): MergeMetadata {
  const targetCells = getAdjacentCells(cell, direction, matrix, locations);
  const canMerge = canMergeCells(cell, targetCells, direction);

  return {
    canMerge,
    targetCells: targetCells.map((_cell) => _cell.id),
    targetMatrix: canMerge
      ? computeTargetMatrix(cell, targetCells, matrix)
      : [],
  };
}

/**
 * Given a Cell and a list of adjacent target Cells, it evaluates if they can be merged.
 *
 * This method is using the location of the cell, defined as gridRowStart,
 * gridColumnStart, gridRowEnd, gridColumnEnd. It could be refactored to look at
 * each cell's coordinates instead. But there's little gain by such refactor
 * given we already have the location data available.
 */
function canMergeCells(
  originCell: Cell | null | undefined,
  targetCells: Array<Cell>,
  direction: CellDirection
): boolean {
  // Early return if no target cells
  if (!originCell || !targetCells || targetCells.length === 0) {
    return false;
  }

  // Union of the target cells, used to compare location with original cell
  const unionTargetCells = {
    id: originCell.id,
    location: computeMergeLocation(targetCells),
  } as const;

  switch (direction) {
    case CELL_DIRECTIONS.TOP:
    case CELL_DIRECTIONS.BOTTOM:
      return (
        areOnSameRow(targetCells) &&
        areOnSameColumn([originCell, unionTargetCells])
      );

    case CELL_DIRECTIONS.LEFT:
    case CELL_DIRECTIONS.RIGHT:
      return (
        areOnSameColumn(targetCells) &&
        areOnSameRow([originCell, unionTargetCells])
      );
    default:
      return false;
  }
}

/**
 * Given a list of Cells, computes the location if all cells were merged
 */
function computeMergeLocation(cells: Array<Cell>): CellLocation {
  let gridRowStart = Infinity;
  let gridColumnStart = Infinity;
  let gridRowEnd = -Infinity;
  let gridColumnEnd = -Infinity;

  for (const cell of cells) {
    gridRowStart = Math.min(gridRowStart, cell.location.gridRowStart);
    gridRowEnd = Math.max(gridRowEnd, cell.location.gridRowEnd);
    gridColumnStart = Math.min(gridColumnStart, cell.location.gridColumnStart);
    gridColumnEnd = Math.max(gridColumnEnd, cell.location.gridColumnEnd);
  }
  return {gridRowStart, gridRowEnd, gridColumnStart, gridColumnEnd};
}

/**
 * Computes the target matrix that would result by merging the given cell with
 * the target cells.
 *
 * Important: must not override original matrix.
 */
function computeTargetMatrix(
  cell: Cell | null | undefined,
  targetCells: Array<Cell>,
  matrix: LayoutMatrix
): LayoutMatrix {
  if (!cell) {
    return matrix;
  }

  let targetMatrix = deepCopyMatrix(matrix);

  targetMatrix = fillLayoutMatrixByLocation(
    targetMatrix,
    cell.location,
    cell.id
  );

  for (const targetCell of targetCells) {
    targetMatrix = fillLayoutMatrixByLocation(
      targetMatrix,
      targetCell.location,
      cell.id
    );
  }
  return targetMatrix;
}

/**
 * Evaluates if all given Cells are on the same column of the Layout
 */
function areOnSameColumn(cells: Array<Cell>): boolean {
  return areAllCellsSameLocation(cells, ['gridColumnStart', 'gridColumnEnd']);
}

/**
 * Evaluates if all given Cells are on the same row of the Layout
 */
function areOnSameRow(cells: Array<Cell>): boolean {
  return areAllCellsSameLocation(cells, ['gridRowStart', 'gridRowEnd']);
}

export default computeMergeCell;
